/* oxlint-disable typescript/no-explicit-any */
import * as client from '@dashhudson/api-client/instagram/client';
import { apiFetch, camelCaseKeys } from '@dashhudson/api-client/runtime';

import type { HttpRequestConfig } from '@/types/http';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';

export const httpCamelCase = createDashHttpClient(env.instagramApiUrl, {
  camelizeKeys: true,
});

type AnyRecord = Record<string, any>;

/** Config shared by generated-client calls and the remaining axios endpoints. */
type RequestConfig = HttpRequestConfig & RequestInit;

async function fetchRawJson<T = AnyRecord>(url: string, config: RequestInit = {}) {
  const body = await apiFetch('instagram', url, config, { responseType: 'text' });
  return body ? (JSON.parse(body) as T) : undefined;
}

function paramsFromUrl(url: string | undefined, fallbackParams: AnyRecord = {}) {
  if (!url) return fallbackParams;

  const parsedUrl = new URL(url, env.instagramApiUrl);
  const urlParams: AnyRecord = {};
  parsedUrl.searchParams.forEach((value, key) => {
    if (urlParams[key] === undefined) {
      urlParams[key] = value;
      return;
    }

    urlParams[key] = Array.isArray(urlParams[key])
      ? [...urlParams[key], value]
      : [urlParams[key], value];
  });

  return { ...fallbackParams, ...(camelCaseKeys(urlParams) as AnyRecord) };
}

async function deleteWithoutBody(url: string, config: RequestConfig = {}) {
  await apiFetch('instagram', url, { ...config, method: 'DELETE' }, { responseType: 'response' });
}

export const updatePost = client.patchBrandsBrandIdInstagramPostsPostId;

export const getInstagramAccount = client.getAccountsBrandId;

export const sendEmailWeeklyReport = client.getBrandsBrandIdInstagramReportsEmailWeekly;
export const sendEmailMonthlyReport = client.getBrandsBrandIdInstagramReportsEmailMonthly;

export const addAccount = client.postInstagramAccountsByHandleInstagramName;

export const getPublicAccountsByIds = client.postInstagramAccountsPublic;

export const getAccountPosts = client.getInstagramAccountsInstagramIdPosts;

export async function getUgcAccounts(
  {
    brandId,
    startDate,
    endDate,
    limit,
    url,
  }: { brandId: number; url?: string } & Partial<
    NonNullable<Parameters<typeof client.getBrandsBrandIdUgcAccounts>[1]>
  >,
  config: RequestConfig = {},
) {
  const params = paramsFromUrl(url, { startDate, endDate, limit });
  return client.getBrandsBrandIdUgcAccounts(
    brandId,
    params as NonNullable<Parameters<typeof client.getBrandsBrandIdUgcAccounts>[1]>,
    config,
  );
}

export const getUgcAccountsCSV = client.postBrandsBrandIdUgcAccountsCsv;

export const getFollowersLost = client.getBrandsBrandIdFollowersLostInsights;

export const getFollowersLostCSV = client.postBrandsBrandIdFollowersLostInsightsCsv;

export const getFollowerInsights = client.getBrandsBrandIdFollowersInsights;

export const getFollowerInsightsCSV = client.postBrandsBrandIdFollowersInsightsCsv;

export const getOnlineFollowers = client.getBrandsBrandIdOnlineFollowers;

export const getOnlineFollowersCSV = client.postBrandsBrandIdOnlineFollowersCsv;

export const getDemographicsInsights = client.getBrandsBrandIdDemographicsInsights;

export const postDemographicsInsightsCSV = client.postBrandsBrandIdDemographicsInsightsCsv;

export const getInstagramUsers = client.getBrandsBrandIdInstagramUsers;

export const getBrandInstagramAccount = client.getBrandsBrandIdInstagramAccount;

export const getInstagramRightsRequestMediaCounts =
  client.getBrandsBrandIdInstagramRightsRequestMediaCounts;

export const getInstagramRightsRequestMediaCsv =
  client.getBrandsBrandIdInstagramRightsRequestMediaCsv;

export const addRightsRequestFacebookComment =
  client.postBrandsBrandIdInstagramRightsRequestMediaByPostIdPostIdFacebookComment;

export const getRightsRequestComments = client.getBrandsBrandIdInstagramRightsRequestComments;

export const addRightsRequestComment = client.postBrandsBrandIdInstagramRightsRequestComments;

export const updateRightsRequestComment = client.patchBrandsBrandIdInstagramRightsRequestCommentsId;

export async function deleteRightsRequestComment(
  { brandId, commentId }: { brandId: number; commentId: number },
  config: RequestConfig = {},
) {
  await deleteWithoutBody(
    client.getDeleteBrandsBrandIdInstagramRightsRequestCommentsIdUrl(brandId, commentId),
    config,
  );
  return { commentId };
}

export const getInstagramUgcComments = client.getBrandsBrandIdInstagramUgcCommentsPostId;

export const getIdealPostTimes = client.getBrandsBrandIdInstagramIdealPostTimes;

export const getRightsRequestMedia = client.getBrandsBrandIdInstagramRightsRequestMedia;

export const getPosts = client.getBrandsBrandIdInstagramPosts;

export async function fetchPostBySourceId(
  { brandId, sourceId }: { brandId: number; sourceId: string | number },
  config: RequestConfig = {},
) {
  return client.postBrandsBrandIdInstagramPosts(
    brandId,
    { ids: [String(sourceId)] },
    { excludeFields: [] },
    config,
  );
}

export const addRelationship = client.postBrandsBrandIdInstagramRelationships;

type RelationshipListParams = NonNullable<
  Parameters<typeof client.getBrandsBrandIdInstagramRelationships>[1]
>;

export async function getRelationshipList(
  {
    brandId,
    url,
    keyword,
    sort,
    publishedStartDate,
    publishedEndDate,
    isBusiness,
    type,
    onlyPostsWithType,
    tagIds,
    tagFilterType,
    isFbConnected,
    acceptanceStatuses,
  }: { brandId: number; url?: string } & Partial<RelationshipListParams>,
  config: RequestConfig = {},
) {
  const params = {
    keyword,
    sort,
    publishedStartDate,
    publishedEndDate,
    isBusiness,
    type,
    onlyPostsWithType,
    tagIds,
    tagFilterType,
    isFbConnected,
    acceptanceStatuses,
  };
  const requestParams = paramsFromUrl(url, params);
  return client.getBrandsBrandIdInstagramRelationships(
    brandId,
    requestParams as RelationshipListParams,
    config,
  );
}

export const exportRelationshipOverviewCSV = client.postBrandsBrandIdInstagramRelationshipsCsv;

export const findRelationshipByHandle = client.getBrandsBrandIdInstagramRelationships;

export const findMediaRelationshipByHandle = client.getBrandsBrandIdInstagramRelationships;

export const findRelationshipByInfluencerId = client.getBrandsBrandIdInstagramRelationships;

export const createRightsRequestMediaPost =
  client.postBrandsBrandIdInstagramRightsRequestMediaByPostIdPostId;

export const bulkCreateRightsRequestMediaPost = client.postBrandsBrandIdInstagramRightsRequestMedia;

export const getRelationship = client.getBrandsBrandIdInstagramRelationshipsRelationshipId;

export const createRelationship = client.postBrandsBrandIdInstagramRelationships;

type UpdateRelationshipBody = NonNullable<
  Parameters<typeof client.patchBrandsBrandIdInstagramRelationshipsRelationshipId>[2]
>;
type UpdateRelationshipParams = NonNullable<
  Parameters<typeof client.patchBrandsBrandIdInstagramRelationshipsRelationshipId>[3]
>;

export async function updateRelationship(
  {
    brandId,
    relationshipId,
    email,
    invitationStatus,
    emailContent,
    influencerId,
    connectInstagramInfluencerToken,
    notes,
    hasPiq,
    newPath,
  }: {
    brandId: number;
    relationshipId: number;
    invitationStatus?: string;
    // Not modeled by the generated client, but accepted by the backend.
    notes?: string | null;
  } & Omit<UpdateRelationshipBody, 'invitationStatus'> &
    UpdateRelationshipParams,
  config: RequestConfig = {},
) {
  const body = { email, invitationStatus, emailContent, notes, hasPiq, newPath };
  const params =
    influencerId && connectInstagramInfluencerToken
      ? { influencerId, connectInstagramInfluencerToken }
      : undefined;
  return client.patchBrandsBrandIdInstagramRelationshipsRelationshipId(
    brandId,
    relationshipId,
    body as UpdateRelationshipBody,
    params,
    config,
  );
}

export const updateRightsRequestMediaPost = client.patchBrandsBrandIdInstagramRightsRequestMediaId;

export async function deleteRightsRequestMediaPost(
  { brandId, id }: { brandId: number; id: number },
  config: RequestConfig = {},
) {
  await deleteWithoutBody(
    client.getDeleteBrandsBrandIdInstagramRightsRequestMediaIdUrl(brandId, id),
    config,
  );
  return { id };
}

export const getRightsRequestDocuments = client.getBrandsBrandIdInstagramRightsRequestDocuments;

export const createRightsRequestDocuments = client.postBrandsBrandIdInstagramRightsRequestDocuments;

export const updateRightsRequestDocuments =
  client.patchBrandsBrandIdInstagramRightsRequestDocumentsId;

export async function deleteRightsRequestDocuments(
  { brandId, id }: { brandId: number; id: number },
  config: RequestConfig = {},
) {
  await deleteWithoutBody(
    client.getDeleteBrandsBrandIdInstagramRightsRequestDocumentsIdUrl(brandId, id),
    config,
  );
  return { id };
}

export const createRightsRequestDocumentsS3Url =
  client.postBrandsBrandIdInstagramRightsRequestDocumentsS3Url;

export const getRightsRequestHashtags = client.getBrandsBrandIdInstagramRightsRequestHashtags;

export const updateRightsRequestHashtags =
  client.patchBrandsBrandIdInstagramRightsRequestHashtagsId;

export async function deleteRelationship(
  { brandId, relationshipId }: { brandId: number; relationshipId: number },
  config: RequestConfig = {},
) {
  await deleteWithoutBody(
    client.getDeleteBrandsBrandIdInstagramRelationshipsRelationshipIdUrl(brandId, relationshipId),
    config,
  );
  return { relationshipId };
}

type RelationshipPostsParams = NonNullable<
  Parameters<typeof client.getBrandsBrandIdInstagramRelationshipsRelationshipIdRelationshipPosts>[2]
>;

export async function getRelationshipPosts(
  {
    brandId,
    type,
    relationshipId,
    onlyVideoPosts,
    publishedStartDate,
    publishedEndDate,
    sort,
    sourceInstagramId,
    isStories,
    url,
  }: {
    brandId: number;
    relationshipId: number;
    url?: string;
    type?: string;
    sort?: string;
    // Not modeled by the generated client, but accepted by the backend.
    onlyVideoPosts?: boolean;
    sourceInstagramId?: string | number;
    isStories?: boolean;
  } & Partial<Omit<RelationshipPostsParams, 'type' | 'sort'>>,
  config: RequestConfig = {},
) {
  const params = {
    type,
    onlyVideoPosts,
    publishedStartDate,
    publishedEndDate,
    sourceInstagramId,
    isStories,
    sort,
  };
  const requestParams = paramsFromUrl(url, params);
  return client.getBrandsBrandIdInstagramRelationshipsRelationshipIdRelationshipPosts(
    brandId,
    relationshipId,
    requestParams as RelationshipPostsParams,
    config,
  );
}

type MentionedStoryPostsParams = NonNullable<
  Parameters<typeof client.getBrandsBrandIdInstagramMentionedStoryPosts>[1]
>;

export async function getRelationshipRIQStoryPosts(
  {
    brandId,
    relationshipId,
    isStories,
    publishedStartDate,
    publishedEndDate,
    sourceInstagramId,
    url,
    sort,
  }: {
    brandId: number;
    url?: string;
    sort?: string;
    // Not modeled by the generated client, but accepted by the backend.
    relationshipId?: number;
    isStories?: boolean;
  } & Partial<Omit<MentionedStoryPostsParams, 'sort'>>,
  config: RequestConfig = {},
) {
  const params = {
    type: 'RIQ',
    relationshipId,
    isStories,
    publishedStartDate,
    publishedEndDate,
    sourceInstagramId,
    sort,
  };
  const requestParams = paramsFromUrl(url, params);
  return client.getBrandsBrandIdInstagramMentionedStoryPosts(
    brandId,
    requestParams as MentionedStoryPostsParams,
    config,
  );
}

export const exportRelationshipRIQStoryPostsCSV =
  client.postBrandsBrandIdInstagramMentionedStoryPostsCsv;

export const getInstagramHashtags = client.getBrandsBrandIdInstagramHashtags;

export const saveInstagramHashtag = client.postBrandsBrandIdInstagramHashtags;

export async function deleteInstagramHashtag(
  { brandId, hashtagId }: { brandId: number; hashtagId: number | string },
  config: RequestConfig = {},
) {
  await deleteWithoutBody(
    client.getDeleteBrandsBrandIdInstagramHashtagsHashtagIdUrl(brandId, Number(hashtagId)),
    config,
  );
  return { hashtagId };
}

export const getUgcPostsCounts = client.getBrandsBrandIdInstagramUgcPostsCounts;

export const getUgcPostsCountsCsv = client.getBrandsBrandIdInstagramUgcPostsPostsCsv;

export const exportRelationshipPostsCSV =
  client.postBrandsBrandIdInstagramRelationshipsRelationshipIdRelationshipPostsCsv;

export const addRelationshipTag = client.postBrandsBrandIdInstagramRelationshipsTags;

export const getBrandRelationshipTags = client.getBrandsBrandIdInstagramRelationshipsTags;

export async function deleteRelationshipTag(
  { brandId, relationshipId, tagId }: { brandId: number; relationshipId: number; tagId: number },
  config: RequestConfig = {},
) {
  await deleteWithoutBody(
    client.getDeleteBrandsBrandIdInstagramRelationshipsTagsTagIdUrl(brandId, tagId, {
      relationshipId,
    }),
    config,
  );
  return { relationshipId, tagId };
}

export const createOrUpdateInfluencer = client.postBrandsBrandIdInstagramRelationshipsInfluencers;

export const getRecentlySearchedHashtags =
  client.getBrandsBrandIdInstagramHashtagsRecentlySearchedHashtags;

export const addMentionUGC = client.postInstagramRelationshipsAddMentionUgc;

export async function getInstagramCustomUtmSettings(
  { brandId }: { brandId: number },
  config: RequestConfig = {},
) {
  // UTM keys/values are user input; must not be (de)camelized.
  return fetchRawJson(client.getGetBrandsBrandIdCustomUrlParamsUrl(brandId), config);
}

export async function saveInstagramCustomUtmSettings(
  { brandId, data: payload }: { brandId: number; data: AnyRecord },
  config: RequestConfig = {},
) {
  // UTM keys/values are user input; must not be (de)camelized.
  const headers = new Headers(config.headers);
  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  return fetchRawJson(client.getPatchBrandsBrandIdCustomUrlParamsUrl(brandId), {
    ...config,
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  });
}

export const getInstagramAccountAllBrands = client.putAccounts;

export const getInteractionInsights = client.getBrandsBrandIdInteractionInsights;

export const postInteractionInsightsCSV = client.postBrandsBrandIdInteractionInsightsCsv;
